import { AssetAmount, AssetRatio } from "@sundaeswap/asset";
import { Utils } from "@sundaeswap/core";
import { TSwapOutcome, getSwapInput, getSwapOutput } from "@sundaeswap/cpp";

import { PoolFragmentFragment } from "../../../gql/generated/stats2.sdk";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetReserve } from "../../../utils/pool.utils";

interface ICalculateFunctionDeps {
  pool?: PoolFragmentFragment;
  ratio?: AssetRatio<IAssetMetaData>;
  isLimitOrder: boolean;
}

export interface ICalculateTakenAmountFromGivenArgs extends ICalculateFunctionDeps {
  given?: AssetAmount<IAssetMetaData>;
  taken?: AssetAmount<IAssetMetaData>;
}

/**
 * Calculates the amount to be taken from the pool based on the amount given, the pool status, and the ratio.
 *
 * @function
 * @param {ICalculateTakenAmountFromGivenArgs} args - The arguments for the calculation.
 * @param {AssetAmount<IAssetMetaData>} args.given - The amount of the asset given.
 * @param {IPool} args.pool - The pool from which the asset is taken.
 * @param {AssetRatio<IAssetMetaData>} args.ratio - The ratio to be used for the calculation.
 * @param {AssetAmount<IAssetMetaData>} args.taken - The initial amount of the asset taken.
 *
 * @returns {AssetAmount<IAssetMetaData>} The calculated amount to be taken.
 *
 * @example
 *
 * const calculatedTaken = calculateTakenAmountFromGiven({
 *   given: new AssetAmount(1000n, assetAMetadata),
 *   pool: poolData,
 *   ratio: assetRatio,
 *   taken: new AssetAmount(0n, assetBMetadata),
 * });
 */
export const calculateTakenAmountFromGiven = ({
  given,
  pool,
  ratio,
  isLimitOrder,
  taken,
}: ICalculateTakenAmountFromGivenArgs) => {
  if (given && given.amount > 0n && pool && !isLimitOrder && taken) {
    const estimatedOutput = getSwapOutput(
      given.amount,
      getAssetReserve(given, pool),
      getAssetReserve(taken, pool),
      Utils.convertPoolFeeToPercent(pool.fee),
      given.decimals === 0
    );

    return new AssetAmount(estimatedOutput.output, taken.metadata);
  } else if (given && ratio && isLimitOrder) {
    return given?.exchangeAt(ratio);
  } else if (given?.amount === 0n && pool) {
    const matchingMetadata =
      taken?.metadata?.assetId === pool.assetA.assetId ? pool.assetA : pool.assetB;
    return new AssetAmount(0n, matchingMetadata);
  }

  return taken;
};

export interface ICalculateGivenFromTakenArgs extends ICalculateFunctionDeps {
  taken: AssetAmount<IAssetMetaData>;
  given?: AssetAmount<IAssetMetaData>;
}

/**
 * Calculates the amount to be given to the pool based on the amount taken, the pool status, and the ratio.
 *
 * @function
 * @param {ICalculateGivenFromTakenArgs} args - The arguments for the calculation.
 * @param {AssetAmount<IAssetMetaData>} args.given - The initial amount of the asset to be given.
 * @param {IPool} args.pool - The pool from which the asset is taken.
 * @param {AssetRatio<IAssetMetaData>} args.ratio - The ratio to be used for the calculation.
 * @param {AssetAmount<IAssetMetaData>} args.taken - The amount of the asset taken.
 *
 * @returns {AssetAmount<IAssetMetaData>} The calculated amount to be given.
 *
 * @example
 *
 * const calculatedGiven = calculateGivenAmountFromTaken({
 *   given: new AssetAmount(0n, assetAMetadata),
 *   pool: poolData,
 *   ratio: assetRatio,
 *   taken: new AssetAmount(1000n, assetBMetadata),
 * });
 */
export const calculateGivenAmountFromTaken = ({
  given,
  pool,
  ratio,
  isLimitOrder,
  taken,
}: ICalculateGivenFromTakenArgs) => {
  if (taken?.amount > 0n && pool && !isLimitOrder && given) {
    let estimatedOutput: Pick<TSwapOutcome, "input"> | undefined;
    try {
      estimatedOutput = getSwapInput(
        taken.amount,
        getAssetReserve(given, pool),
        getAssetReserve(taken, pool),
        Utils.convertPoolFeeToPercent(pool.fee)
      );
    } catch (e) {
      if ((e as Error)?.message === "Output must be less than output reserve") {
        estimatedOutput = {
          input: getAssetReserve(given, pool),
        };
      }
    }
    return new AssetAmount(estimatedOutput?.input || 0, given.metadata);
  } else if (taken && ratio && isLimitOrder) {
    return taken?.exchangeAt(ratio);
  } else if (taken?.amount === 0n && pool) {
    const matchingMetadata =
      given?.metadata?.assetId === pool.assetA.assetId ? pool.assetA : pool.assetB;
    return new AssetAmount(0n, matchingMetadata);
  }

  return given;
};
